<template>
  <fieldset>
    <div class="tla-form-group" v-if="!hideTitle">
      <label class="tla-form-group__label tla-form-group__label--required" for="title" v-html="titleLabel" v-if="!hideLabels" />

      <select
        class="tla-form-group__input tla-form-group__input--arrow"
        :class="getValidationClass('title', 'input')"
        :data-vv-scope="validationScope"
        v-model="dropdownTitle"
        v-validate.initial="'required'"
        name="title"
        data-vv-name="title"
        placeholder="titlePlaceholder">
        <option selected disabled value="null">{{titlePlaceholder}}</option>
        <option v-for="title in titles" :value="title.title" :key="title.id">{{title.title}}</option>
      </select>
      <span class="tla-form-group__message tla-form-group__message--spaced" :class="getValidationClass('title', 'message')" v-if="!hideValidationMessages">{{ errors.first(`${validationScope}.title`) }}</span>
    </div>
    <div v-if="!hideOtherTitle" class="tla-form-group">
      <label class="tla-form-group__label" for="otherTitle" v-if="!hideLabels">{{otherTitleLabel}}</label>
      <input
        id="otherTitle"
        data-vv-name="otherTitle"
        v-model="title"
        class="tla-form-group__input"
        name="otherTitle"
        spellcheck="false"
        @click="logEvent('otherTitle')"
        :placeholder="otherTitlePlaceholder">
    </div>
    <div class="tla-form-group">
      <label class="tla-form-group__label tla-form-group__label--required" for="firstName" v-html="firstNameLabel" v-if="!hideLabels"></label>
      
      <input
        id="firstName"
        v-validate.initial="'required'"
        data-vv-name="firstName"
        :data-vv-scope="validationScope"
        class="tla-form-group__input"
        :class="getValidationClass('firstName', 'input')"
        v-model="firstName"
        name="firstName"
        spellcheck="false"
        @click="logEvent('firstName')"
        :placeholder="firstNamePlaceholder">
        <small class="text-microcopy"><span>{{firstNameMicrocopy}}</span></small>
      <span class="tla-form-group__message tla-form-group__message--spaced" :class="getValidationClass('firstName', 'message')" v-if="!hideValidationMessages">{{ errors.first(`${validationScope}.firstName`) }}</span>
    </div>
    <div class="tla-form-group">
      <label class="tla-form-group__label tla-form-group__label--required" for="lastName" v-html="lastNameLabel" v-if="!hideLabels"></label>

      <input
        id="lastName"
        v-validate.initial="'required'"
        data-vv-name="lastName"
        :data-vv-scope="validationScope"
        autocomplete="family-name"
        type="text"
        class="tla-form-group__input"
        :class="getValidationClass('lastName', 'input')"
        v-model="lastName"
        name="lastName"
        spellcheck="false"
        @click="logEvent('lastName')"
        :placeholder="lastNamePlaceholder">
      <span class="tla-form-group__message tla-form-group__message--spaced" :class="getValidationClass('lastName', 'message')" v-if="!hideValidationMessages">{{ errors.first(`${validationScope}.lastName`) }}</span>
    </div>
    <div class="tla-form-group__title" v-if="contactInformationSectionTitle">
      <label class="tla-form-group__label" for="contactInfoTitle" v-if="!hideLabels">{{contactInformationSectionTitle}}</label>
    </div>
    <div class="tla-form-group" v-if="!isPrePhoneEntry">
      <label class="tla-form-group__label tla-form-group__label--required" for="telephone" v-html="phoneLabel" v-if="!hideLabels"></label>
      <div class="tla-form-group__wrapper">
        <input
          id="telephone"
          autocomplete="tel"
          type="tel"
          v-validate.initial="'required'"
          data-vv-name="telephone"
          :data-vv-scope="validationScope"
          class="tla-form-group__input"
          :class="{'tla-form-group__input--error': (phoneNumberAttemped && errors.first(`${validationScope}.enhanced-telephone`)) || isInvalidVerificationCode}"
          v-model="telephone"
          name="telephone"
          maxlength="20"
          spellcheck="false"
          @click="logEvent('telephone')"
          :placeholder="phonePlaceholder"
          :disabled="phoneVerificationCompleted">
        <span v-if="phoneLoading" class="tla-form-group__wrapper--loading"></span>
      </div>
      <small class="text-microcopy"><span>{{phoneMicroCopyGetter}}</span></small>
      <span class="tla-form-group__message tla-form-group__message--error tla-form-group__message--spaced" v-if="(!hideValidationMessages && phoneNumberAttemped && errors.first(`${validationScope}.enhanced-telephone`))">{{ errors.first(`${validationScope}.enhanced-telephone`) }}</span>
      <span v-if="!hideValidationMessages && isInvalidVerificationCode" class="tla-form-group__message tla-form-group__message--error tla-form-group__message--spaced">Please review the number and try again</span>
    </div>
    <VerificationCodeComponent
      v-if="triggerVerification && useTwilioPhoneVerification"
      :enterCodeText="'Please enter the verification code sent to your phone'"
      :verificationCodeLabel="'Verification Code'"
      :resendCodeText="'Resend code'"
      :overlayHeaderText="'Verification Required'"
      :overlaySubheaderText="'Please check your phone number and submit again'"
      :overlayButtonText="'Resend Code'"
      :validationScope="'personalDetails'"
      :usedFromPersonalDetails="true"
      ref="verificationCodeComponent"
      @code-validated="handleCodeValidation"
    />
    <div class="tla-form-group" v-if="!isEmailValidated">
      <label class="tla-form-group__label tla-form-group__label--required" for="email" v-html="emailLabel" v-if="!hideLabels"></label>
      <div class="tla-form-group__wrapper">
        <input
          id="email"
          autocomplete="email"
          type="email"
          v-validate.initial="'required'"
          data-vv-name="email"
          :data-vv-scope="validationScope"
          class="tla-form-group__input"
          :class="{'tla-form-group__input--error': emailAttemped && errors.first(`${validationScope}.enhanced-email`)}"
          v-model="email"
          name="email"
          maxlength="60"
          spellcheck="false"
          @click="logEvent('email')"
          :placeholder="emailPlaceholder">
        <span v-if="emailLoading" class="tla-form-group__wrapper--loading"></span>
      </div>
      <small class="text-microcopy"><span>{{emailMicroCopyGetter}}</span></small>
      <span class="tla-form-group__message  tla-form-group__message--error tla-form-group__message--spaced" v-if="!hideValidationMessages && emailAttemped && errors.first(`${validationScope}.enhanced-email`)">{{ errors.first(`${validationScope}.enhanced-email`) }}</span>
    </div>
  </fieldset>
</template>

<script lang="ts">
import VeeValidate, {
  FieldError,
  Validator
} from 'vee-validate';
import {
  Component,
  Prop,
  Vue,
  Watch,
  Inject
} from 'vue-property-decorator';
import {
  namespace
} from 'vuex-class';
import Debounce from 'debounce-decorator';
import { policyService } from '@/services/PolicyService';
import Title from '../../models/Title';
import { loggingService } from '../../services/LoggingService';
import { validationService } from '../../services/ValidationService';
import { validationApiService } from '../../services/ValidationApiService';
import PersonalDetailsValidationMessages from '../../validation/personal-details';
import emailValidationService from '../../services/EmailValidationService';
import VerificationCodeComponent from './VerificationCodeComponent.vue';

const EnquiryModule = namespace('enquiryModule');
const PolicyModule = namespace('policyModule');
const PersonalDetailsModule = namespace('personalDetailsModule');

Vue.use(VeeValidate, PersonalDetailsValidationMessages);

@Component({
  components: {
    VerificationCodeComponent
  }
})
export default class PersonalDetailsComponent extends Vue {
  @PolicyModule.Action public submitDefaultEmailMicroCopyText;

  @PolicyModule.Action public submitDefaultPhoneMicroCopyText;

  @PersonalDetailsModule.Action public submitTitle;

  @PersonalDetailsModule.Action public submitFirstName;

  @PersonalDetailsModule.Action public submitLastName;

  @PersonalDetailsModule.Action public submitEmail;

  @PersonalDetailsModule.Action public submitTelephone;

  @PersonalDetailsModule.Action public submitIsValidVerificationCode;

  @PersonalDetailsModule.Getter public personalDetails;

  @PersonalDetailsModule.Getter public isValidVerificationCode;

  @PersonalDetailsModule.Getter public isPrePhoneEntry;

  @Inject('$validator') public $validator!: Validator;

  @Prop({ default: 'We\'ll send you a confirmation email' })
  public emailMicroCopy!: string;

  @Prop({ default: 'We\'ll only use this number to progress your enquiry' })
  public phoneMicroCopy!: string;

  @Prop({ default: false })
  public hideTitle!: boolean;

  @Prop({ default: false })
  public allowOtherTitle!: boolean;

  @Prop({ default: 'Title' })
  public titleLabel!: string;

  @Prop({ default: 'Specify Title (not required)' })
  public otherTitleLabel!: string;

  @Prop({ default: 'First name' })
  public firstNameLabel!: string;

  @Prop({ default: 'Real names help us personalise your pricing information accurately' })
  public firstNameMicrocopy!: string;

  @Prop({ default: 'Last name' })
  public lastNameLabel!: string;

  @Prop({ default: 'Phone' })
  public phoneLabel!: string;

  @Prop({ default: 'Email' })
  public emailLabel!: string;

  @Prop({ default: 'Please select an option' })
  public titlePlaceholder!: string;

  @Prop({ default: 'Enter title here...' })
  public otherTitlePlaceholder!: string;

  @Prop({ default: 'Enter name here...' })
  public firstNamePlaceholder!: string;

  @Prop({ default: 'Enter name here...' })
  public lastNamePlaceholder!: string;

  @Prop({ default: 'Provide number here...' })
  public phonePlaceholder!: string;

  @Prop({ default: 'Provide email here...' })
  public emailPlaceholder!: string;

  @Prop({ default: '' })
  public contactInformationSectionTitle!: string;

  @Prop({ default: null })
  public validationScope!: string;

  @Prop({ default: false })
  public useEnhancedEmailValidation!: boolean;

  @Prop({ default: false })
  public useEnhancedPhoneNumberValidation!: boolean;

  @Prop({ default: false })
  public hideLabels!: boolean;

  @Prop({ default: false })
  public hideValidationMessages!: boolean;

  @Prop({ default: false })
  public useTwilioPhoneVerification!: boolean;

  @Watch('personalDetailsErrors')
  public logEvent(inputFieldName: string) {
    loggingService
      .recordEvent(
        'eventTracking',
        'formEvent',
        'Form Step Personal Details',
        inputFieldName
      );
  }

  private titles: Title[] = [];

  private hideOtherTitle: boolean = true;

  private dropdownTitleValue: string | null = null;

  private phoneNumberAttemped: boolean = false;

  private emailAttemped: boolean = false;

  private emailLoading: boolean = false;

  private phoneLoading: boolean = false;

  private isEmailValidated: boolean = false;

  private invalidPhoneCount: number = 0;

  private triggerVerification: boolean = false;

  private isVerificationCodeValid: boolean = false;

  private phoneVerificationCompleted: boolean = false;

  get title() {
    return this.personalDetails.title;
  }

  set title(otherTitle: string) {
    this.submitTitle(otherTitle);
  }

  get dropdownTitle() {
    return this.dropdownTitleValue;
  }

  set dropdownTitle(title: string | null) {
    if (title === 'Other') {
      this.hideOtherTitle = false;
      this.title = '';
    } else {
      this.hideOtherTitle = true;
      this.submitTitle(title);
    }

    this.dropdownTitleValue = title;
  }

  get firstName() {
    return this.personalDetails.firstName;
  }

  set firstName(fName: string) {
    this.submitFirstName(fName);
  }

  get lastName() {
    return this.personalDetails.surname;
  }

  set lastName(lName: string) {
    this.submitLastName(lName);
  }

  get email() {
    return this.personalDetails.email;
  }

  set email(email: string) {
    this.submitEmail(email);
  }

  get telephone() {
    return this.personalDetails.telephone;
  }

  set telephone(telephone: string) {
    this.submitTelephone(telephone);
  }

  get personalDetailsErrors(): FieldError[] {
    return this.$validator.errors.items;
  }

  public get isInvalidVerificationCode() {
    return !this.isValidVerificationCode;
  }

  public get emailMicroCopyGetter() {
    return policyService.emailMicroCopy;
  }

  public get phoneMicroCopyGetter() {
    return policyService.phoneMicroCopy;
  }

  public get isNextClicked() {
    return validationService.hasNextBeenClicked(this.validationScope);
  }

  @Watch('isNextClicked')
  public watchIsNext() {
    this.watchPhoneNumber();
    this.watchEmail();
    this.phoneNumberAttemped = true;
    this.emailAttemped = true;
  }

  public getValidationClass(fieldName: string, fieldType: string) {
    return validationService
      .getValidationClass(this.$validator, fieldName, fieldType, false, false, this.validationScope);
  }

  @Watch('telephone')
  public async watchPhoneNumber() {
    this.$validator.errors.add({
      field: 'enhanced-telephone',
      msg: 'Please enter a valid UK phone number',
      scope: this.validationScope,
      id: 'enhanced-telephone'
    });
    this.validatePhoneNumber();
  }

  @Debounce(500)
  public async validatePhoneNumber() {
    this.phoneLoading = true;
    this.phoneNumberAttemped = true;
    const regexResult = validationApiService.isPhoneNumberValidRegex(this.telephone);
    if (!regexResult) {
      this.phoneLoading = false;
      this.triggerVerification = false;
      this.validateComponent();
      return;
    }

    const result = await validationApiService.isPhoneNumberValid(this.telephone, this.useEnhancedPhoneNumberValidation);
    const verificationCodeComponent = this.$refs.verificationCodeComponent as any;
    if (result) {
      this.phoneLoading = false;
      this.$validator.errors.removeById('enhanced-telephone');
      this.phoneVerification(verificationCodeComponent);
    } else {
      this.phoneLoading = false;
      this.triggerVerification = false;
      this.invalidPhoneCount += 1;
    }
    if (verificationCodeComponent) {
      verificationCodeComponent.resetStatus();
    }
    this.phoneNumberAttemped = true;
    this.submitIsValidVerificationCode(true);
    this.validateComponent();
  }

  private phoneVerification(verificationCodeComponent: any) {
    if (this.invalidPhoneCount >= 3 && this.useTwilioPhoneVerification) {
      this.triggerVerification = true;
      if (verificationCodeComponent) {
        verificationCodeComponent.sendCode();
      }
      this.$validator.errors.add({
        field: 'verificationCode',
        msg: 'Verification code not set',
        scope: this.validationScope
      });
    }
  }

  @Watch('email')
  public async watchEmail() {
    this.$validator.errors.add({
      field: 'enhanced-email',
      msg: 'Please enter a valid email',
      scope: this.validationScope,
      id: 'enhanced-email'
    });

    this.validateEmail();
  }

  @Debounce(500)
  public async validateEmail() {
    this.emailLoading = true;
    const result = await validationApiService.isEmailValid(this.email, this.useEnhancedEmailValidation);

    if (result) {
      this.emailLoading = false;
      this.$validator.errors.removeById('enhanced-email');
    } else {
      this.emailLoading = false;
    }

    this.emailAttemped = true;
    this.validateComponent();
  }

  public async validateComponent() {
    await this.$validator.validateAll(this.validationScope);
  }

  public async created() {
    this.titles = [
      new Title(1, 'Mr'),
      new Title(2, 'Mrs'),
      new Title(3, 'Ms'),
      new Title(4, 'Miss'),
      new Title(5, 'Prof'),
      new Title(6, 'Dr')
    ];

    if (this.allowOtherTitle) {
      this.titles.push(new Title(7, 'Other'));
    }

    this.$validator.localize('en', PersonalDetailsValidationMessages.english);

    this.submitDefaultEmailMicroCopyText(this.emailMicroCopy);
    this.submitDefaultPhoneMicroCopyText(this.phoneMicroCopy);

    this.isEmailValidated = await this.isEmailAlreadyValidated();
  }

  public async isEmailAlreadyValidated(): Promise<boolean> {
    const isValidated = await emailValidationService.isEmailValidated();
    return isValidated;
  }

  public handleCodeValidation(isValid: boolean) {
    this.isVerificationCodeValid = isValid;

    if (!this.isVerificationCodeValid) {
      this.$validator.errors.add({
        field: 'verificationCode',
        msg: 'Verification code is invalid',
        scope: this.validationScope
      });
    } else {
      this.$validator.errors.remove('verificationCode', this.validationScope);
      this.phoneVerificationCompleted = true;
    }
  }
}
</script>
